import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import api from './services/api'
import { Capacitor } from '@capacitor/core'
import { useAuthStore } from './stores/authStore'
import { useDemoStore } from './stores/demoStore'
import Layout from './components/Layout'
import MobileLayout from './components/MobileLayout'
import MobileHome from './pages/Mobile/Home'
import MobileIncidents from './pages/Mobile/Incidents'
import MobileNewIncident from './pages/Mobile/NewIncident'
import MobileAudits from './pages/Mobile/Audits'
import MobileLicences from './pages/Mobile/Licences'
import MobilePermits from './pages/Mobile/Permits'
import MobileCorrectiveActions from './pages/Mobile/CorrectiveActions'
import LoginPage from './pages/Login'
import RegisterPage from './pages/Register'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Landing from './pages/Landing'
import ChangePassword from './pages/ChangePassword'
import Dashboard from './pages/Dashboard'
import IncidentsList from './pages/Incidents/List'
import IncidentNew from './pages/Incidents/New'
import IncidentDetail from './pages/Incidents/Detail'
import InjuryRegister from './pages/Incidents/Register'
import PermitsList from './pages/Permits/List'
import PermitNew from './pages/Permits/New'
import PermitDetail from './pages/Permits/Detail'
import PsychosocialList from './pages/Psychosocial/List'
import PsychosocialNew from './pages/Psychosocial/New'
import WorkersCompList from './pages/WorkersComp/List'
import CorrectiveActions from './pages/CorrectiveActions'
import CorrectiveActionDetail from './pages/CorrectiveActions/Detail'
import MetricsDashboard from './pages/Metrics'
import SetupWizard from './pages/Setup/Wizard'
import InvestigationsList from './pages/Investigations/List'
import InvestigationNew from './pages/Investigations/New'
import InvestigationDetail from './pages/Investigations/Detail'
import AuditsList from './pages/Audits/List'
import AuditNew from './pages/Audits/New'
import AuditConduct from './pages/Audits/Conduct'
import AuditDetail from './pages/Audits/Detail'
import AuditTemplates from './pages/Audits/Templates'
import AuditTemplateBuilder from './pages/Audits/TemplateBuilder'
import CompetenciesList from './pages/Competencies/List'
import WorkforceList from './pages/Workforce/List'
import WorkerOnboardingDashboard from './pages/Workforce/OnboardingDashboard'
import TeamView from './pages/Workforce/TeamView'
import OnboardingTemplates from './pages/Settings/OnboardingTemplates'
import OnboardingBuilder from './pages/Settings/OnboardingBuilder'
import RolesPage from './pages/Settings/Roles'
import ExternalAccessPage from './pages/Settings/ExternalAccess'
import ContractorsList from './pages/Contractors/List'
import ContractorDetail from './pages/Contractors/Detail'
import AssetsList from './pages/Assets/List'
import UsersSettings from './pages/Settings/Users'
import AdminSettings from './pages/Settings/Admin'
import SimulationSettings from './pages/Settings/Simulation'
import ContractsSettings from './pages/Settings/Contracts'
import IntegrationsSettings from './pages/Settings/Integrations'
import BillingSettings from './pages/Settings/Billing'
import ScheduledReports from './pages/Settings/ScheduledReports'
import RoleNavSettings from './pages/Settings/RoleNav'
import RelationshipsPage from './pages/Settings/Relationships'
import LoneWorkerStart from './pages/LoneWorker/Start'
import LoneWorkerActive from './pages/LoneWorker/Active'
import LoneWorkerMonitor from './pages/LoneWorker/Monitor'
import RiskRegisterList from './pages/RiskRegister/List'
import RiskRegisterDetail from './pages/RiskRegister/Detail'

import NotificationsPage from './pages/Notifications'
import UploadPortal from './pages/Upload/Portal'
import AnalyticsPage from './pages/Analytics/AnalyticsPage'
import AnalyticsDashboard from './pages/Analytics/Dashboard'
import NexusLogin from './pages/Nexus/NexusLogin'
import NexusApp from './pages/Nexus/NexusApp'
import DemoLanding from './pages/Demo/DemoLanding'

const isNative = Capacitor.isNativePlatform()

function LoneWorkerIndex() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [checked, setChecked] = useState(false)
  const adminRoles = ['tenant_admin', 'ops_safety', 'supervisor']

  useEffect(() => {
    api.get('/lone-worker/active')
      .then(({ data }) => {
        if (data) {
          const base = location.pathname.startsWith('/m/') ? '/m/lone-worker' : '/lone-worker'
          navigate(`${base}/active`, { replace: true })
        } else {
          setChecked(true)
        }
      })
      .catch(() => setChecked(true))
  }, [])

  if (!checked) return null
  return adminRoles.includes(user?.role ?? '') ? <LoneWorkerMonitor /> : <LoneWorkerStart />
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

function ImpersonateHandler() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()

  useEffect(() => {
    const token = params.get('token')
    if (!token) return
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      setAuth(token, '', {
        id: payload.id,
        email: payload.email,
        firstName: payload.firstName || 'Admin',
        lastName: payload.lastName || '',
        role: payload.role,
        tenantId: payload.tenantId,
      })
      navigate('/dashboard', { replace: true })
    } catch {
      navigate('/login', { replace: true })
    }
  }, [])

  return null
}

function App() {
  // On every startup: if localStorage has a demo JWT, evict it immediately.
  // This ensures visiting /dashboard directly doesn't stay stuck in demo mode.
  useEffect(() => {
    const { accessToken, logout } = useAuthStore.getState()
    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]))
        if (payload?.isDemo) {
          logout()
          useDemoStore.getState().reset()
        }
      } catch {}
    }
    // If demoStore says demo but auth says not authenticated, just reset demoStore
    if (!useAuthStore.getState().isAuthenticated && useDemoStore.getState().isDemo) {
      useDemoStore.getState().reset()
    }
  }, [])

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={isNative || window.location.hostname === 'demo.hseqintelligence.com' ? <Navigate to="/login" replace /> : <Landing />} />
        <Route path="/nexus/login" element={<NexusLogin />} />
        <Route path="/nexus/*" element={<NexusApp />} />
        <Route path="/demo" element={<DemoLanding />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/change-password" element={<PrivateRoute><ChangePassword /></PrivateRoute>} />
        <Route path="/impersonate" element={<ImpersonateHandler />} />
        <Route path="/upload/:token" element={<UploadPortal />} />
        <Route path="/setup" element={<PrivateRoute><SetupWizard /></PrivateRoute>} />
        {/* Mobile-optimised routes (Capacitor native only) */}
        {isNative && (
          <Route path="/m" element={<PrivateRoute><MobileLayout /></PrivateRoute>}>
            <Route index element={<Navigate to="/m/home" replace />} />
            <Route path="home" element={<MobileHome />} />
            <Route path="incidents" element={<MobileIncidents />} />
            <Route path="incidents/new" element={<MobileNewIncident />} />
            <Route path="incidents/:id" element={<IncidentDetail />} />
            <Route path="audits" element={<MobileAudits />} />
            <Route path="audits/:id/conduct" element={<AuditConduct />} />
            <Route path="audits/:id" element={<AuditDetail />} />
            <Route path="licences" element={<MobileLicences />} />
            {/* More menu sub-pages — rendered inside MobileLayout */}
            <Route path="lone-worker" element={<LoneWorkerIndex />} />
            <Route path="lone-worker/active" element={<LoneWorkerActive />} />
            <Route path="permits" element={<MobilePermits />} />
            <Route path="permits/new" element={<PermitNew />} />
            <Route path="permits/:id" element={<PermitDetail />} />
            <Route path="corrective-actions" element={<MobileCorrectiveActions />} />
            <Route path="corrective-actions/:id" element={<CorrectiveActionDetail />} />
            <Route path="workforce" element={<WorkforceList />} />
            <Route path="settings" element={<Navigate to="/m/settings/admin" replace />} />
            <Route path="settings/admin" element={<AdminSettings />} />
            <Route path="settings/users" element={<UsersSettings />} />
          </Route>
        )}

        <Route element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="incidents" element={<IncidentsList />} />
          <Route path="incidents/new" element={<IncidentNew />} />
          <Route path="incidents/register" element={<InjuryRegister />} />
          <Route path="incidents/:id" element={<IncidentDetail />} />
          <Route path="permits" element={<PermitsList />} />
          <Route path="permits/new" element={<PermitNew />} />
          <Route path="permits/:id" element={<PermitDetail />} />
          <Route path="psychosocial" element={<PsychosocialList />} />
          <Route path="psychosocial/new" element={<PsychosocialNew />} />
          <Route path="workers-comp" element={<WorkersCompList />} />
          <Route path="corrective-actions" element={<CorrectiveActions />} />
          <Route path="corrective-actions/:id" element={<CorrectiveActionDetail />} />
          <Route path="investigations" element={<InvestigationsList />} />
          <Route path="investigations/new" element={<InvestigationNew />} />
          <Route path="investigations/:id" element={<InvestigationDetail />} />
          <Route path="audits" element={<AuditsList />} />
          <Route path="audits/new" element={<AuditNew />} />
          <Route path="audits/templates" element={<AuditTemplates />} />
          <Route path="audits/templates/new" element={<AuditTemplateBuilder />} />
          <Route path="audits/:id" element={<AuditDetail />} />
          <Route path="audits/:id/conduct" element={<AuditConduct />} />
          <Route path="competencies" element={<CompetenciesList />} />
          <Route path="workforce" element={<WorkforceList />} />
          <Route path="workforce/team" element={<TeamView />} />
          <Route path="workforce/:id/onboarding" element={<WorkerOnboardingDashboard />} />
          <Route path="contractors" element={<ContractorsList />} />
          <Route path="contractors/:id" element={<ContractorDetail />} />
          <Route path="assets" element={<AssetsList />} />
          <Route path="metrics" element={<MetricsDashboard />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="analytics/:id" element={<AnalyticsDashboard />} />
          <Route path="settings/users" element={<UsersSettings />} />
          <Route path="settings/admin" element={<AdminSettings />} />
          <Route path="settings/simulation" element={<SimulationSettings />} />
          <Route path="settings/contracts" element={<ContractsSettings />} />
          <Route path="settings/integrations/*" element={<IntegrationsSettings />} />
          <Route path="settings/onboarding" element={<OnboardingTemplates />} />
          <Route path="settings/onboarding/:id/edit" element={<OnboardingBuilder />} />
          <Route path="settings/roles" element={<RolesPage />} />
          <Route path="settings/external-access" element={<ExternalAccessPage />} />
          <Route path="settings/billing" element={<BillingSettings />} />
          <Route path="settings/scheduled-reports" element={<ScheduledReports />} />
          <Route path="settings/role-nav" element={<RoleNavSettings />} />
          <Route path="settings/relationships" element={<RelationshipsPage />} />
          <Route path="lone-worker" element={<LoneWorkerIndex />} />
          <Route path="lone-worker/active" element={<LoneWorkerActive />} />
          <Route path="lone-worker/monitor" element={<LoneWorkerMonitor />} />
          <Route path="risk-register" element={<RiskRegisterList />} />
          <Route path="risk-register/:id" element={<RiskRegisterDetail />} />
          <Route path="notifications" element={<NotificationsPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export default App
